import { useMemo, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Flex, Text } from "@radix-ui/themes";
import { mixTrackEvent, InteractionInformationEvents } from "@/assets/mixpanel";
import { isYotpo } from "@/api/user";
import {
  QaScore,
  ReasonForNegativeScore,
} from "@/v2/entities/qa-score/QaScore.model";
import { QaScoreResult } from "./QaScoreResult";
import { useMutation } from "react-query";
import {
  UpdateConversationScoreRequest,
  useSamplingApi,
} from "@/v2/entities/sampling/Sampling.api";

function formatQuestionTitle(title: string): string {
  return title
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/-/g, " ")
    .toLowerCase()
    .replace(/^\w/, (char) => char.toUpperCase());
}

type ConversationScore = QaScore & {
  onUpdateConversationScore: () => Promise<unknown>;
  viewOnly?: boolean;
};

export function ConversationScore(props: ConversationScore) {
  const { id, title, content, viewOnly } = props;
  const { user } = useAuth0();
  const samplingApi = useSamplingApi();
  const { mutateAsync } = useMutation({
    mutationFn: samplingApi.updateConversationScore,
  });

  const formattedTitle = useMemo(() => formatQuestionTitle(title), [title]);

  const isYotpoUser = useMemo(() => isYotpo(user), [user]);

  const updateConversationScore = useCallback(
    async (newScore: {
      adjustedScore?: number;
      comment?: string;
      reasonsForNegativeScore?: ReasonForNegativeScore[];
    }) => {
      const newConversationScore: UpdateConversationScoreRequest = {
        scoreId: id,
        adjustedScore: newScore.adjustedScore ?? props.adjustedScore,
      };

      if (
        newScore.adjustedScore !== undefined &&
        props.adjustedScore !== newScore.adjustedScore
      ) {
        mixTrackEvent({
          event: InteractionInformationEvents.ADJUSTED_CONVERSATION_AI_SCORE,
          properties: {
            ticketId: content,
            type: props.type,
            oldScore: props.adjustedScore ?? props.autoScore,
            newScore: newScore.adjustedScore,
          },
        });
      }

      await mutateAsync(newConversationScore);
      await props.onUpdateConversationScore();
    },
    [
      mutateAsync,
      props.onUpdateConversationScore,
      props.adjustedScore,
      props.comment,
      id,
      props.reasonsForNegativeScore,
    ]
  );

  return (
    <Flex key={id} direction="column" gap="3">
      {isYotpoUser ? (
        <Flex direction="column">
          <Text size="2" color="gray" weight="medium" highContrast>
            {content}
          </Text>
        </Flex>
      ) : (
        <Flex direction="column">
          <Text size="2" color="gray" weight="medium" highContrast>
            {formattedTitle}
          </Text>
          <Text size="2" color="gray">
            {content}
          </Text>
        </Flex>
      )}

      <QaScoreResult
        {...props}
        viewOnly={viewOnly}
        updateQaScore={updateConversationScore}
        showReasons={isYotpoUser && props.reasonForNegativeScore !== undefined}
        showComment={isYotpoUser && props.comment !== undefined}
      />
    </Flex>
  );
}
